import { motion } from "framer-motion";
import { ShieldCheck, Factory, Snowflake, FileCheck, Microscope, BadgeCheck } from "lucide-react";
import { PageHeader } from "../components/PageHeader";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55 },
};

const STANDARDS = [
  { icon: Factory, title: "World-Class Manufacturing Alliances", text: "Every Cytocare formulation is produced in tie-up with world-class leading manufacturers operating certified, audited facilities." },
  { icon: BadgeCheck, title: "WHO-GMP Compliant Facilities", text: "Partner plants follow Good Manufacturing Practices with documented processes at every stage of production." },
  { icon: Microscope, title: "Batch-Wise Quality Testing", text: "Each batch undergoes rigorous assay, purity and stability verification before it reaches the market." },
  { icon: Snowflake, title: "Cold-Chain Injectable Protocols", text: "Temperature-sensitive oncology injectables and biosimilars are handled under strict cold-chain discipline end to end." },
  { icon: ShieldCheck, title: "Safe Antineoplastic Handling", text: "Cytotoxic formulations follow dedicated containment and handling standards to protect patients and caregivers alike." },
  { icon: FileCheck, title: "Transparent Documentation", text: "Complete product literature, batch records and regulatory documentation available to healthcare partners on request." },
];

const Quality = () => (
  <div data-testid="quality-page">
    <PageHeader
      testId="quality-header"
      eyebrow="Quality & Certifications"
      title="Uncompromising quality, in every single batch"
      subtitle="From antineoplastic injectables to daily nutraceuticals — every formulation meets rigorous standards to ensure safety and efficacy."
    />

    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
      <div className="grid items-center gap-12 lg:grid-cols-2" data-testid="quality-intro">
        <motion.div {...fadeUp} className="overflow-hidden rounded-3xl border border-slate-200 shadow-xl">
          <img
            src="https://images.pexels.com/photos/37839843/pexels-photo-37839843.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Pharmaceutical manufacturing facility"
            className="h-full w-full object-cover"
            data-testid="quality-facility-image"
          />
        </motion.div>
        <motion.div {...fadeUp}>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#1D4ED8]">Our Promise</p>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Quality is not a department. It is the company.
          </h2>
          <p className="mt-5 leading-relaxed text-slate-600">
            As a house built on oncology — where a fraction of a milligram decides outcomes — we
            hold every product to the discipline that cancer care demands. That rigour now extends
            across our gastroenterology, antibiotic, cardio-diabetic and nutraceutical ranges.
          </p>
        </motion.div>
      </div>

      <div className="mt-20 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="quality-standards-grid">
        {STANDARDS.map((s, i) => (
          <motion.div
            key={s.title}
            {...fadeUp}
            transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
            className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm transition-shadow duration-300 hover:shadow-xl"
            data-testid={`quality-standard-${i}`}
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-[#1D4ED8]">
              <s.icon size={22} />
            </div>
            <h3 className="mt-5 text-lg font-bold text-slate-900">{s.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.text}</p>
          </motion.div>
        ))}
      </div>
    </section>
  </div>
);

export default Quality;
